import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { LoginFormData, LoginErrors } from '../types';

interface LoginFormProps {
  onSubmit: (data: LoginFormData) => void;
  isLoading: boolean;
  errors: LoginErrors;
}

const LoginForm = ({ onSubmit, isLoading, errors }: LoginFormProps) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<LoginFormData>({
    emailOrPhone: '',
    password: '',
    rememberMe: false
  });

  const [showPassword, setShowPassword] = useState(false);
  const [localErrors, setLocalErrors] = useState<LoginErrors>({});

  const isEmail = (value: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const isPhone = (value: string): boolean => {
    const v = (value || '').trim();
    const digitsOnly = v.replace(/[^\d]/g, '');
    const isAllDigits = digitsOnly.length > 0 && /^[\d+]+$/.test(v.replace(/\s/g, ''));

    // Match mobile behavior:
    // - Local Vanuatu phone is often 7 digits
    // - Or country format starts with 678 and is >= 10 digits
    if (isAllDigits && digitsOnly.length === 7) return true;
    if (digitsOnly.startsWith('678') && digitsOnly.length >= 10) return true;

    // Fallback: accept E.164-ish input (+ and 7-15 digits)
    if (v.startsWith('+') && digitsOnly.length >= 7 && digitsOnly.length <= 15) return true;

    return false;
  };

  const isNationalId = (value: string): boolean => {
    const v = (value || '').trim();
    const digitsOnly = v.replace(/[^\d]/g, '');
    const isAllDigits = digitsOnly.length > 0 && /^[\d+]+$/.test(v.replace(/\s/g, ''));

    // Match mobile behavior:
    // National ID is numeric-only and short (commonly 5-8 digits), but NOT local phone.
    if (!isAllDigits) return false;
    if (digitsOnly.length === 7) return false; // 7 digits = treat as phone (Vanuatu local)
    return digitsOnly.length >= 5 && digitsOnly.length <= 8;
  };

  const validateEmailOrPhone = (value: string): string | undefined => {
    const v = (value || '').trim();
    if (!v) return 'Email, National ID, or phone number is required';
    
    if (isEmail(v)) {
      return undefined; // Valid email
    }
    
    if (isPhone(v)) {
      return undefined; // Valid phone
    }

    if (isNationalId(v)) {
      return undefined; // Valid national ID (heuristic)
    }
    
    return 'Please enter a valid email, National ID, or phone number';
  };

  const validatePassword = (password: string): string | undefined => {
    if (!password) return 'Password is required';
    if (password.length < 6) return 'Password must be at least 6 characters';
    return undefined;
  };

  const handleInputChange = (field: keyof LoginFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'emailOrPhone' && typeof value === 'string') {
      const error = validateEmailOrPhone(value);
      setLocalErrors(prev => ({ ...prev, emailOrPhone: error }));
    }
    
    if (field === 'password' && typeof value === 'string') {
      const error = validatePassword(value);
      setLocalErrors(prev => ({ ...prev, password: error }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const emailOrPhoneError = validateEmailOrPhone(formData.emailOrPhone);
    const passwordError = validatePassword(formData.password);
    
    if (emailOrPhoneError || passwordError) {
      setLocalErrors({
        emailOrPhone: emailOrPhoneError,
        password: passwordError
      });
      return;
    }

    // Clear local errors and submit to parent for Supabase authentication
    setLocalErrors({});
    onSubmit(formData);
  };


  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {(errors.general || localErrors.general) && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-3">
          <Icon name="AlertCircle" size={20} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-destructive font-medium">
              {errors.general || localErrors.general}
            </p>
          </div>
        </div>
      )}

      <Input
        type="text"
        label="Email / National ID / Phone"
        placeholder="Enter your email, National ID, or phone"
        value={formData.emailOrPhone}
        onChange={(e) => handleInputChange('emailOrPhone', e.target.value)}
        error={localErrors.emailOrPhone || errors.emailOrPhone}
        required
        disabled={isLoading}
        className="w-full"
      />

      <div className="relative">
        <Input
          type={showPassword ? 'text' : 'password'}
          label="Password"
          placeholder="Enter your password"
          value={formData.password}
          onChange={(e) => handleInputChange('password', e.target.value)}
          error={localErrors.password || errors.password}
          required
          disabled={isLoading}
          className="w-full"
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-interactive"
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
        </button>
      </div>

      <div className="flex items-center justify-between">
        <Checkbox
          label="Remember me"
          checked={formData.rememberMe}
          onChange={(e) => handleInputChange('rememberMe', e.target.checked)}
          disabled={isLoading}
        />
        <button
          type="button"
          onClick={() => navigate('/forgot-password')}
          className="text-sm text-primary hover:text-primary/80 transition-interactive font-medium"
        >
          Forgot Password?
        </button>
      </div>

      <Button
        type="submit"
        variant="default"
        size="lg"
        fullWidth
        loading={isLoading}
        iconName="LogIn"
        iconPosition="right"
      >
        Sign In
      </Button>
    </form>
  );
};

export default LoginForm;